<template>
  <LoadingScreen v-if="isLoading" />
</template>

<script>
import { mapActions, mapState } from 'vuex'

import * as Sentry from '@sentry/vue'
import { authentication, app } from '@microsoft/teams-js'
import config from '@/config'
import constant from '@/constant'
import cookie from '@/services/cookie'
import {
  scheduleShowEnjoyTrialPopup,
  scheduleShowTrialNotAvailablePopup,
  isPlanDifferentFromTrial,
} from '@/utils/trial-plan.utils'
import BulkLicenseMixin from '@/mixins/BulkLicenseMixin.js'

import Util from '@/util'
import AuthApi from '@/api/auth'
import LoadingScreen from '@/components-v2/common/loading/LoadingScreen.vue'

const {
  GOOGLE_SIGNIN_REDIRECT,
  GOOGLE_SIGNIN_SAVE_SPINNER_WHEEL,
  GOOGLE_SIGNIN_SAVE_WORDCLOUD,
} = constant.storageKeys

export default {
  name: 'Auth',
  components: {
    LoadingScreen,
  },
  mixins: [BulkLicenseMixin],

  data: () => {
    return {
      isLoading: true,
      identifyProvider: '',
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
    }),
  },
  async mounted() {
    this.identifyProvider = this.$route.query.provider
    if (this.identifyProvider) {
      this.handleCreateUserUsingIdentityProvider(this.identifyProvider)
      return
    }
    this.$router.replace({ name: 'Login' })
  },
  methods: {
    ...mapActions(['setErrorOnInternalServerAction', 'setCurrentUserAction', 'createCheckoutSessionAction']),

    async getTokenAndUserForProvider(provider) {
      let res = {}
      if (provider === constant.identityProviders.GOOGLE || provider === constant.identityProviders.MICROSOFT) {
        const referrer = cookie.getValue('referrer')
        const source = window.localStorage.getItem(constant.storageKeys.GOOGLE_SIGNIN_SOURCE)
        this.$route.query.referrer = referrer
        this.$route.query.source = source
        this.$route.query.firstPage = Util.getCookie('ahaFirstPage')
        if (provider === constant.identityProviders.GOOGLE) {
          res = await AuthApi.loginWithGoogle(this.$route.query)
        } else if (provider === constant.identityProviders.MICROSOFT) {
          res = await AuthApi.loginWithMicrosoft(this.$route.query)
        }
      }

      if (provider === constant.identityProviders.SKOLETUBE) {
        const returnedData = this.$route.query.return_data
        res = await this.$http.post(config.api.createSkoletubePresenter, { returnedData })
        res = res.body
      }

      return res
    },

    async handleCreateUserUsingIdentityProvider(provider) {
      try {
        const { token, isNew, user } = await this.getTokenAndUserForProvider(provider)
        if (!token) throw new Error('Token does not exist')
        if (provider === constant.identityProviders.MICROSOFT && window.opener) {
          window.location.href = `${config.powerpointCallBack}/pages/token?token=${token}&isPopup=true`
          return
        }
        const isGuest = cookie.getValue('aha-guest-user')
        this._handleUserToken(token, isNew)
        await this._handleGuestUser(user, isGuest)

        const redirect = window.localStorage.getItem(GOOGLE_SIGNIN_REDIRECT)
        const saveWordcloud = window.localStorage.getItem(GOOGLE_SIGNIN_SAVE_WORDCLOUD)
        const saveSpinnerwheel = window.localStorage.getItem(GOOGLE_SIGNIN_SAVE_SPINNER_WHEEL)
        if (await this._handleMSTeams(token)) return

        await this._handleTrialLink(user)

        if (await this._handleBulkLicenseCheckoutPage(user)) return

        Util.handleAudienceTrackingAuthentication(isGuest, isNew, provider, redirect)
        this._handleRedirect(redirect, saveWordcloud, saveSpinnerwheel)
        this._handleNewUser(isNew, saveWordcloud, saveSpinnerwheel)
        this._handlePresenterTrackingAuthentication(user, isGuest, isNew, provider, redirect)
      } catch (error) {
        Sentry.captureException(error)
        this.trackGA('failed')
        this.handleErrorForProvider(provider)
      }
    },

    _handleUserToken(token, isNew) {
      Util.removeFirstPageCookie()
      Util.setCookieToken(token)

      if (isNew) cookie.setValue('aha-new-user', true)
    },

    async _handleGuestUser(user, isGuest) {
      if (!isGuest) return

      cookie.deleteValue('aha-guest-user')

      if (user && user.id && this.currentUser.id) {
        await this.setCurrentUserAction(user)
      }
    },

    async _handleMSTeams(token) {
      const googleSource = window.localStorage.getItem(constant.storageKeys.GOOGLE_SIGNIN_SOURCE)
      const isFromMSTeams = Util.getParamValue('ms_teams', googleSource)

      if (isFromMSTeams) {
        await app.initialize()
        authentication.notifySuccess(token)

        return true
      }

      return false
    },

    async _handleTrialLink(user) {
      const isTrialLink = window.localStorage.getItem('isTrialLink') == 'true'
      if (isTrialLink) {
        window.localStorage.removeItem('isTrialLink')

        if (user.hasUpgradedToTrial) {
          scheduleShowEnjoyTrialPopup(user)
        } else if (isPlanDifferentFromTrial(user)) {
          scheduleShowTrialNotAvailablePopup(user)
        }
      }
    },

    async _handleBulkLicenseCheckoutPage(user) {
      const canRedirectToBulkLicenseCheckoutPage = await this.canRedirectToBulkLicenseCheckoutPage(user)

      if (canRedirectToBulkLicenseCheckoutPage) return true

      return false
    },

    _handleRedirect(redirect, saveWordcloud, saveSpinnerwheel) {
      if (redirect) {
        window.localStorage.removeItem(GOOGLE_SIGNIN_REDIRECT)
        window.location.href = redirect
      } else if (saveWordcloud) {
        this.handleSavePresentation(GOOGLE_SIGNIN_SAVE_WORDCLOUD, 'wordcloud')
      } else if (saveSpinnerwheel) {
        this.handleSavePresentation(GOOGLE_SIGNIN_SAVE_SPINNER_WHEEL, 'spinnerwheel')
      } else {
        this.$router.replace({ name: 'Home' })
      }
    },

    handleSavePresentation(storageKey, queryKey) {
      window.localStorage.removeItem(storageKey)
      const query = { [queryKey]: true }
      this.$router.push({ path: '/save-presentation', query })
    },

    _handleNewUser(isNew, saveWordcloud, saveSpinnerwheel) {
      if (isNew && !saveWordcloud && !saveSpinnerwheel) {
        this.$store.state.isComeFromSignupPage = true
      }

      if (isNew) window.dataLayer.push({ event: 'signup' })
    },

    _handlePresenterTrackingAuthentication(user, isGuest, isNew, provider, redirect = '') {
      const decodedRedirect = decodeURIComponent(redirect)

      if (decodedRedirect.includes(config.audienceDomain)) return

      if (config.region !== constant.euRegion) {
        this.trackGA('success', user, isGuest, isNew, provider)
        Util.trackGA4AndMixpanel('login', { method: `with ${provider}` })
        Util.ga4Config({ user_id: user.id })
      }
    },

    handleErrorForProvider(provider) {
      let errorMsgTitle = 'Login error'
      if (provider === constant.identityProviders.GOOGLE) {
        errorMsgTitle = constant.errors.GOOGLE_LOGIN_ERROR
        setTimeout(() => {
          this.$router.replace({ name: 'Login' })
          this.setErrorOnInternalServerAction(null) // close error popup after redirect
        }, 3000)
      }
      if (provider === constant.identityProviders.SKOLETUBE) {
        errorMsgTitle = constant.errors.SKOLETUBE_LOGIN_ERROR
      }
      this.setErrorOnInternalServerAction(errorMsgTitle) // show global error popup in App.vue
      this.isLoading = false
    },

    trackGA(status, data, asGuest, isNew, identifyProvider) {
      const action = status === 'success' ? 'Success' : 'Failed'
      const from = window.localStorage.getItem(constant.storageKeys.GOOGLE_SIGNIN_FROM)

      // Presenter mới toanh chưa có tài khoản trên AhaSlides,
      // nhưng lại click vào “Login With Google” thì nên record thành event Signup ở GA/MixPanel.
      // Ngược lại, nếu presenter đã có tài khoản rồi
      // mà ấn Signup = Google thì sẽ tính là Login event

      if (status === 'success') {
        if (isNew) {
          if (asGuest) {
            Util.mixpanelTracking(
              constant.eventCategories.PRESENTER_REGISTER,
              'Sign up',
              'Demo with Google',
            )
            Util.trackGA4AndMixpanel('sign_up', {
              method: 'Demo with Google',
            })
          } else {
            Util.mixpanelTracking(
              constant.eventCategories.PRESENTER_REGISTER,
              'Sign up',
              `with ${identifyProvider}`,
            )
            Util.trackGA4AndMixpanel('sign_up', {
              method: `with ${identifyProvider}`,
            })
          }
        } else {
          Util.mixpanelTracking(
            constant.eventCategories.PRESENTER_REGISTER,
            'Login',
            `with ${identifyProvider}`,
          )
        }
      } else {
        Util.mixpanelTracking(
          constant.eventCategories.PRESENTER_REGISTER,
          `${from} > ${action}`,
          `with ${identifyProvider}`,
        )
      }
    },
  },
}
</script>
